/**
 * Core lib application - "the powerhouse"
 */
'use strict';

var Backbone = require('backbone');
var _ = require('underscore');
var Constructor = require('js/vendor/constructor');
var Router = require('js/lib/router');
var Controller = require('js/lib/controller');
var Composer = require('js/lib/composer');
var Promise = require('js/vendor/promise');
var React = require('react');
var ReactDom = require('react-dom');
import App from 'jsx/app';

// Some consts
var ACTION_SUFFIX = 'Action';

/**
 * Core lib Application
 *
 * @exports Application
 * @constructor
 * @alias module:lib/app
 */
var Application = Constructor.extend().mixes(Backbone.Events);

Application.prototype.options = {};

/**
 * Hold this applications router
 */
Application.prototype.router = null;

/**
 * The current controller instance
 */
Application.prototype.controller = null;

/**
 * A 'composer' isntance global to this app
 */
Application.prototype.composer = null;

Application.prototype.loadingController = null;

/**
 * Hold the root react view (the 'app' component)
 */
Application.prototype.app = null; // Rendered component or null
Application.prototype.appComponent = null; // Potentially un-rendered

/**
 * A shallow copy of the most recent router resolution to be resolved.
 * NOTE: This may be modified by the application (or re routed from controller actions)
 *       so does not neccasarily equal this.router.res
 * @type {Object|null}
 */
Application.prototype.res = null;
Application.prototype.lastRes = null; // and store last resolution

Application.prototype.container = null;

/**
 * Holds the current state object
 * @type {State|null}
 */
Application.prototype.state = null;

/**
 * Application initializer
 * @consructs Application
 */
Application.prototype.initialize = function(options) {
  options = this.options = options || {}; // TODO: Merge options with defaults

  this.container = options.container || document.getElementById('container');

  // Create our app view component
  this.appComponent = React.createElement(App, {
    app: this,
  });

  // Initialize router instance
  this.router = new Router(options.routes);

  // Initialize global composer
  this.composer = new Composer(this);
};

/**
 * Start pushstate history, run initial route, render first layout etc.
 */
Application.prototype.start = function() {
  // Listen for when our router resolves a route

  // Jaime  dirty fix
  this.listenTo(this.router, 'resolve', this.dispatch, this);

  // Start pushstate monitoring
  Backbone.history.start({ pushState: true, root: '/' });

  // Render our app into the dom
  this.app = ReactDom.render(this.appComponent, this.container);

  // Trigger start event
  this.trigger('app:start');
};

/**
 * Parse a resolved resolution (by default takes a shallow copy of router res)
 * @return {Object} Parsed resolution object
 */
Application.prototype.parseRes = function(res) {
  return _.clone(res);
};

/**
 * Handle a router resolution by loading the appropiate controller and action.
 * Will pass named parameters map to controller action when called.
 */
Application.prototype.dispatch = function(res) {
  // Store last res
  this.lastRes = this.res;

  // Set the new router resolution
  var appRes = this.res = this.parseRes(res);

  // Run a soft invalidation on composed items
  this.composer.invalidate();

  // Attempt to load the correct controller
  return this.loadController(appRes.controller, appRes.module).then(function(controller) {

    if (!controller) {
      return Promise.broken(new Error('Something went really wrong on controller load!'));
    }

    // Initialize new controller
    if (!(this.controller instanceof controller)) {
      // Destruct any old controller we may have
      if (this.controller) {
        this.controller.destroy();
      }

      // Initialize the new controller
      this.controller = new controller(this);

      // Trigger a new controller event
      this.trigger('dispatch:controller', this.controller);

    } else {
      // Invalidate composer (soft)
      this.controller.composer.invalidate();
    }

    return this.callAction(appRes.action, appRes.params);

  }, this).then(null, function(error) {

    this.show404(error);

  }, this);
};

/**
 * Load a given controller
 */
Application.prototype.loadController = function(controller, moduleName) {
  var promise = this.loadingController = new Promise();
  var load;

  try {
    if (moduleName) {
      load = require('bundle!js/modules/' + moduleName + '/controllers/' + controller);
    } else {
      load = require('bundle!js/controllers/' + controller);
    }

    load(function(newController) {
      promise.fulfil(newController);
    });
  } catch (err) {
    promise.break(new Error('Failed to load controller. ' + err.message));
  }

  return promise;
};

/**
 * Call defined action on the current controller, passing through params.
 */
Application.prototype.callAction = function(action, params) {
  var state;

  // If we actually have the action method wanted
  var actionMethodName = action + ACTION_SUFFIX;
  if (this.controller[actionMethodName]) {

    // Call the target action
    state = this.controller[actionMethodName](params);

    // Allow chaining of dispatches from controller actions (Ie. allow controller actions to be async)
    if (state instanceof Promise) {

      state.then(function(payload) {

        // If we have been given a controller state then just set it
        if (payload instanceof Controller.State) {
          this.setState(payload);
          // If we have been given a router resolution then lets dispatch it
        } else if (payload instanceof Router.Resolution) {
          this.dispatch(payload);
          // Else we have no idea how to handle this returned value (return true means we have handled elsewhere)
        } else if (!payload) {
          return Promise.broken(new Error('Invalid return value from action with name "' + action + '" on controller "' + this.res.controller + '"'));
        }

      }, this);

      // If we have been given a controller state then just set it
    } else if (state instanceof Controller.State) {
      this.setState(state);
      // If we have been given a router resolution then lets dispatch it
    } else if (state instanceof Router.Resolution) {
      this.dispatch(state);
      // Else we have no idea how to handle this returned value (return true means we have handled elsewhere)
    } else if (!state) {
      state = Promise.broken(new Error('Invalid return value from action with name "' + action + '" on controller "' + this.res.controller + '"'));
    }

    // Trigger a new controller event
    this.trigger('dispatch:action', this.controller, action);

  } else {

    // Allow error to propogate to single handler
    state = Promise.broken(new Error('No action found with name "' + action + '" on controller "' + this.res.controller + '"'));

  }

  return state;
};

/**
 * Set current state object on the application
 * @param {ControllerState} state A controller State instance
 */
Application.prototype.setState = function(state) {
  // Store current state
  this.state = state;

  // Set the page as defined by the state
  this.updatePage(state.page, state.data);
};

/**
 * Set the given page contents (pass a react instance)
 */
Application.prototype.updatePage = function(page, pageData) {
  const isReduxPageId = pageData.isReduxPageId;
  this.app.setState({
    page: page,
    pageData: Object.assign({}, pageData, { key: isReduxPageId ? isReduxPageId : _.uniqueId('page') }),
  });
};

/**
 * Show a 404 error page
 */
Application.prototype.show404 = function(error) {

  this.trigger('dispatch:404', error);

  this.showError(error, 404);
};

/**
 * Show an error page
 */
Application.prototype.showError = function(error) {
  this.trigger('dispatch:error', error);
};

module.exports = Application;




// WEBPACK FOOTER //
// ./app/assets/js/lib/app.js